import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { RedirectRequest, PopupRequest, AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { BehaviorSubject, filter, Observable, takeUntil, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  loginSubject = new BehaviorSubject<boolean>(false);
  loginUser: { name: string, email: string } = { name: '', email: '' };

  isLoggedIn(): Observable<boolean> {
    return this.loginSubject.asObservable();
  }

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private router: Router
  ) { 
    this.checkAndSetActiveAccount();
    this.msalBroadcastService.msalSubject$
      .pipe(
        tap((msg: EventMessage) => {
          console.log('MESSAGE STATUS', msg)
        }),
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.LOGOUT_SUCCESS)
      )
      .subscribe((result: EventMessage) => {
        console.log(result);
        if (result.eventType === EventType.LOGIN_SUCCESS) {
          this.checkAndSetActiveAccount();
          this.router.navigateByUrl('/');
        } else {
          this.router.navigateByUrl('/landing');
        }
      });

    // this.msalBroadcastService.msalSubject$
    //   .pipe(
    //     filter(
    //       (msg: EventMessage) =>
    //         msg.eventType === EventType.ACCOUNT_ADDED ||
    //         msg.eventType === EventType.ACCOUNT_REMOVED
    //     )
    //   )
    //   .subscribe((result: EventMessage) => {
    //     if (this.authService.instance.getAllAccounts().length === 0) {
    //       window.location.pathname = '/';
    //     } else {
    //       this.setLoginDisplay();
    //     }
    //   });

    this.msalBroadcastService.inProgress$
      .pipe(
        tap((status: InteractionStatus) => {
          console.log('INTERACTION STATUS', status)
        }),
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        // takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }

  setLoginDisplay() {
    const accounts = this.authService.instance.getAllAccounts();
    if (accounts.length === 0) {
      this.loginUser = { name: '', email: '' };
      this.loginSubject.next(false);
      return;
    } else {
      this.loginUser = { name: accounts[0].name ?? '', email: accounts[0].username ?? '' };
      this.loginSubject.next(true);
    }
  }


  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.authService.instance.getActiveAccount();

    if (
      !activeAccount &&
      this.authService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
    }
    this.setLoginDisplay();
  }

  loginRedirect(): Observable<void> {
    if (this.msalGuardConfig.authRequest) {
      return this.authService.loginRedirect({
        ...this.msalGuardConfig.authRequest,
      } as RedirectRequest);
    } else {
      return this.authService.loginRedirect();
    }
  }

  loginPopup(): Observable<AuthenticationResult> {
    if (this.msalGuardConfig.authRequest) {
      return this.authService
        .loginPopup({ ...this.msalGuardConfig.authRequest } as PopupRequest)
        .pipe(tap((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        }));
    } else {
      return this.authService
        .loginPopup()
        .pipe(tap((response: AuthenticationResult) => {
          this.authService.instance.setActiveAccount(response.account);
        }));
    }
  }

  logout(popup?: boolean) {
    if (popup) {
      return this.authService.logoutPopup({
        mainWindowRedirectUri: '/',
      });
    } else {
      let activeAccount = this.authService.instance.getActiveAccount();
      return this.authService.logoutRedirect({
        account: activeAccount,
      });
    }
  }
}
